/**
 * Admin V3 — Commercial Work Queue controls (F-1)
 *
 * Shared, presentational controls for the Devis / Bon de commande operator
 * work queue: a status filter, a free-text search box, and an offset/limit
 * pager. Used so an operator can reliably DISCOVER pending commercial work
 * (e.g. ACCEPTED Devis, DRAFT BdC) and so objects never disappear beyond the
 * list limit. State is owned by the page; these are controlled components.
 */
import React from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { t, DEFAULT_LOCALE } from '../i18n';

const inputCls =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

/** Status filter + free-text search. `statusMeta` is the visualTokens meta map
 *  (its keys are the selectable statuses, labels are French). */
export const WorkQueueToolbar = ({
  statusValue, onStatusChange, statusMeta,
  search, onSearchChange, testidPrefix, locale = DEFAULT_LOCALE,
}) => {
  const options = Object.keys(statusMeta || {});
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid={`${testidPrefix}-toolbar`}>
      <select
        value={statusValue}
        onChange={(e) => onStatusChange(e.target.value)}
        aria-label={t('queue.filter.label', locale)}
        data-testid={`${testidPrefix}-status-filter`}
        className={inputCls}
      >
        <option value="all">{t('queue.filter.all', locale)}</option>
        {options.map((k) => (
          <option key={k} value={k}>{statusMeta[k]?.label || k}</option>
        ))}
      </select>
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('queue.search.placeholder', locale)}
          aria-label={t('queue.search.label', locale)}
          data-testid={`${testidPrefix}-search`}
          className={`w-full pl-8 ${inputCls}`}
        />
      </div>
    </div>
  );
};

/** Offset/limit pager. Hidden when everything fits on one page. */
export const WorkQueuePager = ({
  offset, limit, total, shown, onPrev, onNext, testidPrefix, locale = DEFAULT_LOCALE,
}) => {
  const safeTotal = Number(total) || 0;
  const start = safeTotal === 0 ? 0 : offset + 1;
  const end = offset + (Number(shown) || 0);
  const canPrev = offset > 0;
  const canNext = offset + limit < safeTotal;
  if (!canPrev && !canNext) return null; // single page → no pager
  return (
    <div className="flex items-center justify-between pt-1" data-testid={`${testidPrefix}-pager`}>
      <span className="text-[12px] text-slate-500" data-testid={`${testidPrefix}-pager-info`}>
        {t('queue.page.info', locale).replace('{shown}', `${start}–${end}`).replace('{total}', String(safeTotal))}
      </span>
      <div className="flex gap-2">
        <button
          type="button" disabled={!canPrev} onClick={onPrev}
          data-testid={`${testidPrefix}-prev`}
          className="inline-flex items-center rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-3.5 w-3.5 mr-1" />{t('queue.page.prev', locale)}
        </button>
        <button
          type="button" disabled={!canNext} onClick={onNext}
          data-testid={`${testidPrefix}-next`}
          className="inline-flex items-center rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('queue.page.next', locale)}<ChevronRight className="h-3.5 w-3.5 ml-1" />
        </button>
      </div>
    </div>
  );
};
